// SIPP-729: Story Mode Screen 2 — Your Water Signals (Figma redesign)
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import {
  WaterQualityIndexSelectors,
  UIStatus,
  WQIGroupKey,
  GroupedCategory,
} from '../../../store/waterQualityIndex'
import {
  WQI_GROUP_LABELS,
  WQI_GROUP_ORDER,
} from '../../../store/waterQualityIndex/groupLabels'

const GROUP_DESCRIPTIONS: Record<WQIGroupKey, string> = {
  health_safety: 'Measures acute and chronic health risks from contaminants.',
  home_infrastructure: 'Assesses corrosion, scale buildup, and pipe damage risks.',
  skin_laundry: 'Evaluates irritants and minerals that affect personal care.',
  taste_odor: 'Checks for factors that impact water taste and odor.',
}

const CATEGORY_CARDS: {
  groupKey: WQIGroupKey
  name: string
  description: string
}[] = WQI_GROUP_ORDER.map((groupKey) => ({
  groupKey,
  name: WQI_GROUP_LABELS[groupKey],
  description: GROUP_DESCRIPTIONS[groupKey],
}))

type StatusConfig = {
  label: string
  colorKey: 'success' | 'warning' | 'error'
}

const STATUS_MAP: Record<UIStatus, StatusConfig> = {
  safe: { label: 'Safe', colorKey: 'success' },
  cautious: { label: 'Cautious', colorKey: 'warning' },
  unsafe: { label: 'Unsafe', colorKey: 'error' },
}

const StatusPill = ({ category }: { category: GroupedCategory | undefined }) => {
  const theme = useTheme()

  if (!category) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 99,
          bgcolor: theme.palette.grey[100],
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: theme.palette.grey[400],
          }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: theme.palette.grey[500],
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Typography>
      </Box>
    )
  }

  const config = STATUS_MAP[category.status]
  const palette = theme.palette[config.colorKey]

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: 99,
        bgcolor: palette.light,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: palette.main,
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: palette.dark,
          lineHeight: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {config.label}
      </Typography>
    </Box>
  )
}

export const StoryScreen2 = () => {
  const theme = useTheme()
  const categories = useSelector(WaterQualityIndexSelectors.selectContributorsByUICategory)
  const categoryMap = new Map(categories.map((c) => [c.groupKey, c]))

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        p: 4,
        gap: 3,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(24px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        animation: 'fadeInUp 0.5s ease-out both',
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
          Your Water Signals
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Signals are derived from EPA and utility data run through the SIPP Water Quality Engine.
          They reflect patterns across detected contaminants in your water supply.
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {CATEGORY_CARDS.map(({ groupKey, name, description }) => {
          const category = categoryMap.get(groupKey)

          return (
            <Box
              key={groupKey}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: '8px',
                p: '14px',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.75 }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {name}
                </Typography>
                <StatusPill category={category} />
              </Stack>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {description}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
